import {
    DATE_FORMAT,
    DateField,
    IReservation,
    IReservationsShiftGroupWithGuests,
    IRestaurantConfiguration,
    ISpeedDialActionProps,
    RESERVATIONS_FILTERS_COLLAPSE_STATE,
    RESERVATIONS_NOW_LINE_ENABLED,
    ReservationUtils,
    RestaurantUtils,
    Segmented,
    SERVER_DATE_FORMAT,
    SpeedDial,
    Switch,
    TextField,
    TIME_FORMAT,
} from '@localina/core';
import { BookNewIcon, WalkinIcon } from '@localina/icons';
import { Chip, Collapse } from '@mui/material';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SwipeableViews from 'react-swipeable-views';
import { useRestaurant } from '../../api/queries/restaurants';
import { Page, ReservationsShiftGroup } from '../../components';
import CollapseButton from '../../components/CollapseButton/CollapseButton';
import { CollapseStage, useLocalStorageCollapseStage } from '../../components/CollapseButton/localStorageCollapseState';
import ExportReservationsButton from '../../components/ExportReservationsButton/ExportReservationsButton';
import { ReservationsEmptyState } from '../../components/ReservationsEmptyState';
import RestaurantDailyNote from '../../components/RestaurantDailyNote/RestaurantDailyNote';
import TablePlanReservationsSwitchButton from '../../components/TablePlanReservationsSwitchButton/TablePlanReservationsSwitchButton';
import { UserFeature } from '../../components/UserFeature';
import { IReservationForm } from '../../schemas/interfaces';
import {
    filterReservations,
    UNASSIGNED_RESERVATION_SHIFT_FILTER_TAB_ID,
    useReservationActions,
    useReservationsList,
} from '../../utils/ReservationsUtils';
import { ReservationsOccupancy } from './ReservationsOccupancy';

const Reservations: React.FC = () => {
    const { t } = useTranslation();
    const restaurantQuery = useRestaurant();

    const { openCreateReservationModal, openEditReservationModal, openWalkinReservationModal } =
        useReservationActions();
    const {
        reservationsQuery,
        filters,
        setFilters,
        filterOptions,
        onBlurSearchField,
        dateTime,
        swipeIndex,
        noReservations,
    } = useReservationsList(false);

    const { stage: filtersStage, cycleStage: cycleFiltersStage } = useLocalStorageCollapseStage(
        RESERVATIONS_FILTERS_COLLAPSE_STATE,
    );

    const now = useNowEveryMinute();
    const isToday = dateTime.hasSame(now, 'day');
    const [nowLineEnabled, setNowLineEnabled] = useNowLineEnabled();

    const restaurantConfiguration = restaurantQuery.data?.configuration;
    const shiftsMapping = RestaurantUtils.getShiftsForDate(dateTime, restaurantConfiguration);

    const reservationsData = useMemo(() => {
        if (reservationsQuery.data?.reservations) {
            const resData = shiftsMapping.reduce((acc, shift) => {
                const reservations = filterReservations(
                    reservationsQuery.data.reservations,
                    restaurantConfiguration,
                    {
                        shiftId: shift.id,
                        showCancelled: filters.cancelled === 'true',
                        searchQuery: filters.search || '',
                        areaId: filters.areaId || '',
                    },
                    t('reservations.view.fields.walkin'),
                );

                if (reservations.length) {
                    acc[shift.id] = {
                        reservations,
                        guests: ReservationUtils.sumTotalParticipantsOfReservations(reservations),
                        shift: shift.shift,
                    };
                }
                return acc;
            }, {} as IReservationsShiftGroupWithGuests);

            const unassignedReservations = filterReservations(
                reservationsQuery.data.reservations,
                restaurantConfiguration,
                {
                    shiftId: filterOptions.constants.unassignedShift.value,
                    showCancelled: filters.cancelled === 'true',
                    searchQuery: filters.search || '',
                    areaId: filters.areaId || '',
                },
                t('reservations.view.fields.walkin'),
            );

            if (unassignedReservations.length) {
                resData[filterOptions.constants.unassignedShift.value] = {
                    reservations: unassignedReservations,
                    guests: ReservationUtils.sumTotalParticipantsOfReservations(unassignedReservations),
                };
            }

            return resData;
        } else {
            return undefined;
        }
    }, [
        restaurantConfiguration,
        reservationsQuery.data?.reservations,
        filters.cancelled,
        filters.areaId,
        filters.search,
        filters.shiftId,
    ]);

    // each service pill carries its number of reservations, following the area, search and cancelled filters
    const shiftOptions = useMemo(
        () =>
            filterOptions.shifts.map((option) => {
                if (!reservationsData) {
                    return option;
                }
                const count =
                    option.value === filterOptions.constants.defaultShift.value
                        ? Object.values(reservationsData).reduce(
                              (sum, { reservations }) => sum + reservations.length,
                              0,
                          )
                        : reservationsData[option.value]?.reservations.length ?? 0;
                return {
                    ...option,
                    label: (
                        <>
                            {option.label}
                            <span className="reservations-filter-pills__count">{count}</span>
                        </>
                    ),
                };
            }),
        [filterOptions.shifts, reservationsData],
    );

    const createActions: ISpeedDialActionProps[] = [
        {
            label: t('reservations.actions.createReservation'),
            icon: <BookNewIcon />,
            onClick: openCreateReservationModal,
        },
        {
            label: t('reservations.actions.createWalkin'),
            icon: <WalkinIcon />,
            onClick: openWalkinReservationModal,
        },
    ];

    const visibleReservations = useMemo(() => {
        if (!reservationsData) {
            return [];
        }
        if (filters.shiftId && filterOptions.constants.defaultShift.value !== filters.shiftId) {
            return reservationsData[filters.shiftId]?.reservations ?? [];
        }
        return Object.values(reservationsData).flatMap(({ reservations }) => reservations);
    }, [filters.areaId, filters.shiftId, reservationsData]);

    const totalNumberOfGuests = useMemo(() => {
        let result = 0;
        if (reservationsData) {
            if (filters.shiftId && filterOptions.constants.defaultShift.value !== filters.shiftId) {
                result = reservationsData[filters.shiftId]?.guests || 0;
            } else {
                result = Object.values(reservationsData).reduce((acc, { guests }) => acc + guests, 0);
            }
        }
        return result;
    }, [filters.areaId, filters.shiftId, reservationsData]);

    const shiftLabelById = Object.fromEntries(filterOptions.shifts.map(({ value, label }) => [value, label]));

    const labelOf = (options: { label: string; value: string }[], value: string, defaultValue: string) =>
        value && value !== defaultValue ? options.find((option) => option.value === value)?.label : undefined;

    const activeFilters = {
        shift: labelOf(filterOptions.shifts, filters.shiftId, filterOptions.constants.defaultShift.value),
        area: labelOf(filterOptions.areas, filters.areaId, filterOptions.constants.defaultArea.value),
        search: filters.search || undefined,
    };

    // With a service selected and empty, offer the service that holds the most of the day's reservations.
    const switchTargetShift = useMemo(() => {
        if (!activeFilters.shift || !reservationsData) {
            return undefined;
        }
        const [best] = Object.entries(reservationsData)
            .filter(([shiftId]) => shiftId !== filters.shiftId)
            .map(([shiftId, { reservations }]) => ({ shiftId, count: reservations.length }))
            .sort((a, b) => b.count - a.count);
        const label = best && filterOptions.shifts.find(({ value }) => value === best.shiftId)?.label;
        return best && label ? { ...best, label } : undefined;
    }, [activeFilters.shift, reservationsData, filters.shiftId, filterOptions.shifts]);

    // the switch only appears when the visible shift(s) would show a now line, whether or not it is on
    const hasNowLine = useMemo(() => {
        if (!isToday || !reservationsData) {
            return false;
        }
        const shiftIds =
            filters.shiftId && filterOptions.constants.defaultShift.value !== filters.shiftId
                ? [filters.shiftId]
                : Object.keys(reservationsData);
        return shiftIds.some((shiftId) => findNowLine([shiftId], reservationsData, now) !== undefined);
    }, [isToday, reservationsData, filters.shiftId, now]);

    const onSwipeableViewChange = (value: string | number) => {
        if (restaurantConfiguration) {
            if (typeof value === 'string') {
                setFilters({ shiftId: value });
            } else {
                setFilters({ shiftId: filterOptions.shifts[value].value });
            }
        }
    };

    return (
        <Page name="reservations" isLoading={reservationsQuery.isInitialLoading}>
            <div className="reservations-header">
                <div className="reservations-header__content">
                    <div className="daily-note-wrapper">
                        <UserFeature filter="tablePlans">
                            <div className="reservations-header__switch">
                                <TablePlanReservationsSwitchButton switchTo={'tableplan'} />
                            </div>
                        </UserFeature>
                        {Boolean(dateTime) && <RestaurantDailyNote date={dateTime.toFormat(SERVER_DATE_FORMAT)} />}
                    </div>
                    <div className={'reservations-date-notifications-wrapper'}>
                        <div className="reservations-date">
                            <DateField
                                name="date"
                                label={t('reservations.fields.date')}
                                value={dateTime.toFormat(DATE_FORMAT)}
                                shouldHighlightDate={(calendarDate: DateTime) =>
                                    RestaurantUtils.isClosedAndHasNoShiftsOnDate(calendarDate, restaurantConfiguration)
                                        ? 'restaurant-closed-day'
                                        : false
                                }
                                onChange={(value) => {
                                    setFilters({
                                        date: DateTime.fromFormat(value, DATE_FORMAT).toFormat(SERVER_DATE_FORMAT),
                                    });
                                }}
                                withChevrons
                                withTodayButton
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="reservations-body">
                <div className="reservations-collapsible-filters">
                    <Collapse
                        in={
                            filtersStage !== CollapseStage.Collapsed &&
                            Boolean(filterOptions.areas.length > 1 || filterOptions.shifts.length > 1)
                        }
                    >
                        <div className="reservations-filter-pills">
                            {Boolean(filterOptions.shifts.length > 1) && (
                                <Segmented
                                    label={t('reservations.filters.shift')}
                                    value={filters.shiftId || filterOptions.constants.defaultShift.value}
                                    options={shiftOptions}
                                    onChange={onSwipeableViewChange}
                                    scrollable
                                />
                            )}
                            {Boolean(filterOptions.areas.length > 1) && (
                                <UserFeature filter="areas">
                                    <Segmented
                                        label={t('reservations.filters.area')}
                                        value={filters.areaId || filterOptions.constants.defaultArea.value}
                                        options={filterOptions.areas}
                                        onChange={(areaId) => {
                                            setFilters({ areaId });
                                        }}
                                        scrollable
                                    />
                                </UserFeature>
                            )}
                        </div>
                    </Collapse>
                    {}
                    <Collapse
                        in={
                            filtersStage === CollapseStage.Expanded &&
                            Boolean(filterOptions.areas.length > 1 || filterOptions.shifts.length > 1)
                        }
                    >
                        <div className="reservations-body__bottom-toolbar">
                            {Boolean(filterOptions.areas.length > 1 || filterOptions.shifts.length > 1) && (
                                <div className="reservations-body__bottom-toolbar__text-filter">
                                    <TextField
                                        name={'reservations-filter'}
                                        label={t('reservations.searchReservations')}
                                        value={filters.search || ''}
                                        onChange={(search) => {
                                            setFilters({ search });
                                        }}
                                        allowClear
                                        onBlur={onBlurSearchField}
                                    />
                                </div>
                            )}
                            {Boolean(filterOptions.areas.length > 1 || filterOptions.shifts.length > 1) && (
                                <div className="reservations-body__bottom-toolbar__switch">
                                    <div className="reservations-body__bottom-toolbar__switches">
                                        <Switch
                                            label={t('reservations.showCancelled')}
                                            onChange={(cancelled) => {
                                                setFilters({ cancelled: cancelled ? 'true' : 'false' });
                                            }}
                                            checked={filters.cancelled === 'true'}
                                        />
                                        {hasNowLine && (
                                            <Switch
                                                label={t('reservations.showNowLine')}
                                                onChange={setNowLineEnabled}
                                                checked={nowLineEnabled}
                                            />
                                        )}
                                    </div>
                                    <div className="reservations-body__bottom-toolbar__button">
                                        <ExportReservationsButton
                                            dateTime={dateTime}
                                            filters={{
                                                shiftId: filters.shiftId || filterOptions.constants.defaultShift.value,
                                                areaId: filters.areaId || filterOptions.constants.defaultArea.value,
                                                searchQuery: filters.search || '',
                                                showCancelled: filters.cancelled === 'true',
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </Collapse>
                    {Boolean(filterOptions.areas.length > 1 || filterOptions.shifts.length > 1) && (
                        <CollapseButton
                            collapsed={filtersStage === CollapseStage.Collapsed}
                            onToggle={cycleFiltersStage}
                            label={t('reservations.filters.toggle')}
                        />
                    )}
                </div>

                {restaurantConfiguration && (
                    <>
                        <SwipeableViews
                            className="swipeable-views"
                            index={swipeIndex}
                            onChangeIndex={onSwipeableViewChange}
                            disableLazyLoading
                        >
                            {filterOptions.shifts.map((shiftTab, shiftTabIndex) => {
                                const shiftNowLine =
                                    isToday && nowLineEnabled && reservationsData
                                        ? findNowLine([shiftTab.value], reservationsData, now)
                                        : undefined;
                                return (
                                    <div key={shiftTab.value} className="reservations-body__content">
                                        <ReservationsEmptyState
                                            configuration={restaurantConfiguration}
                                            dateTime={dateTime}
                                            noReservations={noReservations}
                                            noFilteredReservations={!totalNumberOfGuests}
                                            activeFilters={activeFilters}
                                            switchTarget={
                                                switchTargetShift && {
                                                    label: switchTargetShift.label,
                                                    count: switchTargetShift.count,
                                                    onClick: () => onSwipeableViewChange(switchTargetShift.shiftId),
                                                }
                                            }
                                            onClearFilters={() => {
                                                setFilters({
                                                    shiftId: '',
                                                    areaId: '',
                                                    search: '',
                                                });
                                            }}
                                            onCreate={() =>
                                                openCreateReservationModal({ date: [dateTime.toFormat(DATE_FORMAT)] })
                                            }
                                            onNextDay={() =>
                                                setFilters({
                                                    date: dateTime.plus({ days: 1 }).toFormat(SERVER_DATE_FORMAT),
                                                })
                                            }
                                        />
                                        {Boolean(shiftTab.value === filterOptions.constants.defaultShift.value) && (
                                            <AllDayTab
                                                reservationsData={reservationsData}
                                                shiftLabelById={shiftLabelById}
                                                restaurantConfiguration={restaurantConfiguration}
                                                openEditReservationModal={openEditReservationModal}
                                                openCreateReservationModal={openCreateReservationModal}
                                                isToday={isToday}
                                                nowLineEnabled={nowLineEnabled}
                                                now={now}
                                                active={shiftTabIndex === swipeIndex}
                                            />
                                        )}
                                        {Boolean(
                                            filterOptions.constants.defaultShift.value !== shiftTab.value &&
                                                Boolean(reservationsData?.[shiftTab.value]),
                                        ) && (
                                            <>
                                                {shiftTab.value === filterOptions.constants.unassignedShift.value && (
                                                    <UnassignedReservationsInfo />
                                                )}
                                                <ReservationsShiftGroup
                                                    {...reservationsData![shiftTab.value]}
                                                    label={shiftLabelById[shiftTab.value]}
                                                    restaurantConfiguration={restaurantConfiguration}
                                                    onCreate={openCreateReservationModal}
                                                    onEdit={openEditReservationModal}
                                                    unassignedShift={
                                                        shiftTab.value === filterOptions.constants.unassignedShift.value
                                                    }
                                                    isToday={isToday}
                                                    nowISO={now.toISO() ?? undefined}
                                                    nowLineBeforeDatetime={shiftNowLine?.beforeDatetime}
                                                    nowLineVisible={shiftNowLine !== undefined}
                                                    nowLineScrollIntoView={shiftTabIndex === swipeIndex}
                                                />
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </SwipeableViews>
                    </>
                )}
            </div>
            <div className="reservations-fab-anchor">
                <SpeedDial label="create" actions={createActions} />
            </div>
            <ReservationsOccupancy reservations={visibleReservations} />
        </Page>
    );
};

const useNowEveryMinute = () => {
    const [now, setNow] = React.useState(() => DateTime.local());
    React.useEffect(() => {
        const intervalId = setInterval(() => setNow(DateTime.local()), 60_000);
        return () => clearInterval(intervalId);
    }, []);
    return now;
};

// a per-device preference, on by default; storage can be unavailable (private mode), so it falls back to on
const useNowLineEnabled = () => {
    const [enabled, setEnabled] = React.useState(() => {
        try {
            return window.localStorage.getItem(RESERVATIONS_NOW_LINE_ENABLED) !== 'false';
        } catch {
            return true;
        }
    });
    const update = React.useCallback((value: boolean) => {
        setEnabled(value);
        try {
            window.localStorage.setItem(RESERVATIONS_NOW_LINE_ENABLED, String(value));
        } catch {
            // keep the in-memory value only
        }
    }, []);
    return [enabled, update] as const;
};

const findNowLine = (
    orderedShiftIds: string[],
    reservationsData: IReservationsShiftGroupWithGuests,
    now: DateTime,
): { ownerShiftId: string; beforeDatetime: string } | undefined => {
    let earliestUpcoming: string | undefined;
    orderedShiftIds.forEach((shiftId) => {
        reservationsData[shiftId]?.reservations.forEach((reservation) => {
            if (ReservationUtils.isReservationInDeadStatus(reservation)) {
                return;
            }
            const reservationTime = DateTime.fromISO(reservation.reservationDateTime);
            if (reservationTime >= now && (!earliestUpcoming || reservationTime < DateTime.fromISO(earliestUpcoming))) {
                earliestUpcoming = reservation.reservationDateTime;
            }
        });
    });

    if (earliestUpcoming === undefined) {
        return undefined;
    }

    const ownerShiftId = orderedShiftIds.find((shiftId) =>
        reservationsData[shiftId]?.reservations.some(
            (reservation) => reservation.reservationDateTime === earliestUpcoming,
        ),
    );
    if (ownerShiftId === undefined) {
        return undefined;
    }

    const ownerHasPastReservation = reservationsData[ownerShiftId].reservations.some(
        (reservation) =>
            !ReservationUtils.isReservationInDeadStatus(reservation) &&
            DateTime.fromISO(reservation.reservationDateTime) < now,
    );
    if (!ownerHasPastReservation) {
        return undefined;
    }

    return { ownerShiftId, beforeDatetime: earliestUpcoming };
};

interface IAllDayTabProps {
    restaurantConfiguration?: IRestaurantConfiguration;
    reservationsData?: IReservationsShiftGroupWithGuests;
    shiftLabelById: Record<string, string>;
    openCreateReservationModal: (defaultFormValues?: Partial<IReservationForm>) => void;
    openEditReservationModal: (reservation: IReservation) => void;
    isToday?: boolean;
    nowLineEnabled?: boolean;
    now?: DateTime;
    active?: boolean;
}

const AllDayTab = (props: IAllDayTabProps) => {
    const sortedShiftIds = sortShiftIdsInAllShiftsTab(props.reservationsData);
    const nowLineByShiftId = Object.fromEntries(
        sortedShiftIds.map((key) => [
            key,
            props.isToday && props.nowLineEnabled && props.now && props.reservationsData
                ? findNowLine([key], props.reservationsData, props.now)
                : undefined,
        ]),
    );
    // several shifts can have a now line at once; only the topmost one scrolls into view
    const scrollingShiftId = sortedShiftIds.find((key) => nowLineByShiftId[key] !== undefined);
    return (
        <>
            {Boolean(sortedShiftIds.length && sortedShiftIds.includes(UNASSIGNED_RESERVATION_SHIFT_FILTER_TAB_ID)) && (
                <UnassignedReservationsInfo />
            )}
            {sortedShiftIds.map((key) => {
                const shiftNowLine = nowLineByShiftId[key];
                return (
                    <ReservationsShiftGroup
                        key={key}
                        {...props.reservationsData![key]}
                        label={props.shiftLabelById[key]}
                        restaurantConfiguration={props.restaurantConfiguration!}
                        onCreate={props.openCreateReservationModal}
                        onEdit={props.openEditReservationModal}
                        unassignedShift={key === UNASSIGNED_RESERVATION_SHIFT_FILTER_TAB_ID}
                        isToday={props.isToday}
                        nowISO={props.now?.toISO() ?? undefined}
                        nowLineBeforeDatetime={shiftNowLine?.beforeDatetime}
                        nowLineVisible={shiftNowLine !== undefined}
                        nowLineScrollIntoView={props.active && key === scrollingShiftId}
                    />
                );
            })}
        </>
    );
};

const sortShiftIdsInAllShiftsTab = (reservationsData?: IReservationsShiftGroupWithGuests) => {
    return reservationsData
        ? Object.keys(reservationsData)
              .slice()
              .sort((a, b) => {
                  const unassignedA = a === UNASSIGNED_RESERVATION_SHIFT_FILTER_TAB_ID;
                  const unassignedB = b === UNASSIGNED_RESERVATION_SHIFT_FILTER_TAB_ID;
                  if (unassignedA && !unassignedB) {
                      return -1;
                  }
                  if (unassignedB && !unassignedA) {
                      return 1;
                  }
                  if (!reservationsData[a].shift?.from) {
                      return 1;
                  }
                  if (!reservationsData[b].shift?.from) {
                      return -1;
                  }
                  return DateTime.fromFormat(reservationsData[a].shift!.from, TIME_FORMAT).diff(
                      DateTime.fromFormat(reservationsData[b].shift!.from, TIME_FORMAT),
                  ).milliseconds;
              })
        : [];
};

const UnassignedReservationsInfo = () => {
    const { t } = useTranslation();
    return (
        <Chip
            className="unassigned-reservations-info"
            label={t('reservations.fields.unassignedReservationsInfoMessage')}
            variant="outlined"
        />
    );
};

export default Reservations;
